// ZoneLivreursEditScreen.jsx - Edit livreurs for a specific zone
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Table, Badge, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import ClickBack from "../../components/buttons/ClickBack";
import {
  fetchZoneLivreurs,
  fetchAllLivreurs,
  assignZoneToLivreur,
  updateZoneAssignment,
  deleteZoneAssignment,
} from "./dataService";
import IconTrash from "../../images/trash.png";

function ZoneLivreursEditScreen() {
  const { id } = useParams(); // zone ID
  const history = useHistory();
  const [zone, setZone] = useState(null);
  const [currentLivreurs, setCurrentLivreurs] = useState([]);
  const [allLivreurs, setAllLivreurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLivreurData, setNewLivreurData] = useState({
    livreur_id: "",
    is_primary: false,
    priority: 1,
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current livreurs for this zone
      const zoneResp = await fetchZoneLivreurs(id);
      setZone(zoneResp.zone);
      setCurrentLivreurs(zoneResp.livreurs || []);

      // Fetch all available livreurs
      const allLivreursResp = await fetchAllLivreurs();
      setAllLivreurs(allLivreursResp.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAssignment = async (assignmentId, field, value) => {
    try {
      // If updating is_primary to true, we need to unset others first
      if (field === "is_primary" && value) {
        // Update locally
        setCurrentLivreurs(
          currentLivreurs.map((l) => ({
            ...l,
            is_primary: l.id === assignmentId,
          }))
        );
      }

      await updateZoneAssignment(assignmentId, { [field]: value });

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error updating assignment:", err);
      alert("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir retirer ce livreur de cette zone ?")) {
      return;
    }

    try {
      await deleteZoneAssignment(assignmentId);
      await fetchData(); // Refresh
      alert("Livreur retiré avec succès");
    } catch (err) {
      console.error("Error deleting assignment:", err);
      alert("Erreur lors de la suppression");
    }
  };

  const handleAddLivreur = async () => {
    if (!newLivreurData.livreur_id) {
      alert("Veuillez sélectionner un livreur");
      return;
    }

    try {
      // Assign the zone to the selected livreur
      await assignZoneToLivreur(newLivreurData.livreur_id, {
        zone_id: id,
        is_primary: newLivreurData.is_primary,
        priority: newLivreurData.priority,
        notes: newLivreurData.notes,
      });
      setShowAddModal(false);
      setNewLivreurData({ livreur_id: "", is_primary: false, priority: 1, notes: "" });
      await fetchData(); // Refresh
      alert("Livreur assigné avec succès");
    } catch (err) {
      console.error("Error adding livreur:", err);
      alert(err.response?.data?.message || "Erreur lors de l'assignation");
    }
  };

  // Get livreurs that are not yet assigned to this zone
  const availableLivreurs = useMemo(() => {
    if (!Array.isArray(allLivreurs)) return [];
    return allLivreurs.filter(
      (livreur) => !currentLivreurs.some((cl) => cl.livreur_id === livreur.id)
    );
  }, [allLivreurs, currentLivreurs]);

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => history.goBack()}>Retour</Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">
            Modifier les livreurs de la zone : {zone?.name}
          </h2>
          <p className="text-muted">
            Statut de la zone : <Badge bg={zone?.status === "active" ? "success" : "secondary"}>{zone?.status || "-"}</Badge>
          </p>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Livreurs assignés ({currentLivreurs.length})</h5>
              <Button size="sm" variant="light" onClick={() => setShowAddModal(true)}>
                + Ajouter un livreur
              </Button>
            </Card.Header>
            <Card.Body>
              {currentLivreurs.length === 0 ? (
                <Alert variant="info">
                  Aucun livreur assigné. Cliquez sur "Ajouter un livreur" pour commencer.
                </Alert>
              ) : (
                <Table striped bordered>
                  <thead>
                    <tr>
                      <th>Livreur</th>
                      <th>Type</th>
                      <th>Priorité</th>
                      <th>Statut</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLivreurs.map((assignment) => (
                      <tr key={assignment.id}>
                        <td>
                          <strong>
                            {assignment.livreur?.nom} {assignment.livreur?.prenom}
                          </strong>
                        </td>
                        <td>
                          <Form.Check
                            type="radio"
                            label="Principale"
                            name={`primary_livreur_${id}`}
                            checked={assignment.is_primary}
                            onChange={() =>
                              handleUpdateAssignment(assignment.id, "is_primary", true)
                            }
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={assignment.priority}
                            onChange={(e) =>
                              handleUpdateAssignment(assignment.id, "priority", parseInt(e.target.value))
                            }
                          >
                            {[1, 2, 3, 4, 5].map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={assignment.is_active}
                            onChange={(e) =>
                              handleUpdateAssignment(assignment.id, "is_active", e.target.checked)
                            }
                            label={assignment.is_active ? "Active" : "Inactive"}
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            size="sm"
                            value={assignment.notes || ""}
                            onChange={(e) =>
                              handleUpdateAssignment(assignment.id, "notes", e.target.value)
                            }
                            placeholder="Notes..."
                          />
                        </td>
                        <td>
                          <Button
                            variant="link"
                            className="p-0"
                            onClick={() => handleDeleteAssignment(assignment.id)}
                            title="Supprimer"
                          >
                            <img src={IconTrash} alt="Supprimer" width="20" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Livreur Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assigner un nouveau livreur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {availableLivreurs.length === 0 ? (
            <Alert variant="warning">
              Tous les livreurs disponibles sont déjà assignés à cette zone.
            </Alert>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Livreur</Form.Label>
                <Form.Select
                  value={newLivreurData.livreur_id}
                  onChange={(e) =>
                    setNewLivreurData({ ...newLivreurData, livreur_id: e.target.value })
                  }
                >
                  <option value="">Sélectionner un livreur...</option>
                  {availableLivreurs.map((livreur) => (
                    <option key={livreur.id} value={livreur.id}>
                      {livreur.nom} {livreur.prenom}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Zone principale pour ce livreur"
                  checked={newLivreurData.is_primary}
                  onChange={(e) =>
                    setNewLivreurData({ ...newLivreurData, is_primary: e.target.checked })
                  }
                />
                <Form.Text className="text-muted">
                  Le livreur ne peut avoir qu'une seule zone principale
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Priorité</Form.Label>
                <Form.Select
                  value={newLivreurData.priority}
                  onChange={(e) =>
                    setNewLivreurData({ ...newLivreurData, priority: parseInt(e.target.value) })
                  }
                >
                  {[1, 2, 3, 4, 5].map((p) => (
                    <option key={p} value={p}>
                      {p} {p === 1 && "(Haute)"} {p === 5 && "(Basse)"}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  1 = Priorité haute, 5 = Priorité basse
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={newLivreurData.notes}
                  onChange={(e) =>
                    setNewLivreurData({ ...newLivreurData, notes: e.target.value })
                  }
                  placeholder="Notes administratives..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Annuler
          </Button>
          {availableLivreurs.length > 0 && (
            <Button variant="primary" onClick={handleAddLivreur}>
              Assigner
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ZoneLivreursEditScreen;
